import { FC } from 'react';
import { Col, Row } from 'reactstrap';
import { useLocation } from 'react-router-dom'; // React Router for handling queries
import { clx } from 'Utils';
import { PublishJobItem } from 'Types';
import { FontAwesome, Highlighter, ShimmerImage } from 'Elements';
import styles from 'partials/card/list-job-card.module.scss';
import styled from 'styled-components';
import { useResponsive } from 'containers/Hooks';

interface Props {
	job: PublishJobItem;
	className?: string;
}

export const ListJobCard: FC<Props> = ({ job, className }) => {
	const location = useLocation();
	const queryParam = new URLSearchParams(location.search).get('q') ?? '';
	const { isMobileAndBelow } = useResponsive();

	const Tag = styled.div`
		background-color: #f5f5f5;
		background-image: linear-gradient(180deg, #efeff2 0%, #f8f8f9 100%);
		color: #7d7d7d;
		padding: 3px 16px;
		border-radius: 8px;
		font-size: 13px;
		font-weight: 500;
		text-align: center;
		display: inline-block;
	`;

	const defaultImage = '/assets/img/default-image.png';

	return (
		<article className={clx(styles.card, className)}>
			<Row>
				<Col md={4}>
					<div className={clx(styles.imageBox, 'd-block w-100')}>
						<ShimmerImage
							src={job?.featuredPhoto}
							defaultImage={defaultImage}
							alt={job?.title}
							className={styles.image}
							width={500}
							height={500}
						/>
					</div>
				</Col>
				<Col md={8}>
					<div className="d-flex flex-column h-100 gap-2">
						<div
							className={clx(
								styles.content,
								'd-flex flex-column flex-grow-1 gap-1'
							)}>
							<h2 className="h4 mb-0 lh-sm">
								<div className="d-inline-flex text-gray-6">
									<Highlighter
										text={job.title}
										search={queryParam}
									/>
								</div>
							</h2>

							<div className="d-flex justify-content-start align-items-baseline text-center gap-2 text-gray-3">
								<FontAwesome icon="location-dot" />
								<div
									style={{
										fontSize: '14px'
									}}>
									{job?.locations
										?.map((item) => item?.title)
										.join(' | ')}
								</div>
							</div>
							{job?.shortDescription && (
								<div
									className={clx(
										styles.description,
										'lh-base text-gray-3'
									)}>
									<Highlighter
										text={job?.shortDescription}
										search={queryParam}
									/>
								</div>
							)}
							<div className="d-flex flex-wrap gap-2 ">
								{job.jobTags?.map((tag, i) => (
									<Tag key={i}>
										<span>{tag?.title?.split('|')[2]}</span>
									</Tag>
								))}
							</div>
						</div>
						<div className="d-flex align-items-center text-gray-3 gap-3">
							<div className="d-flex align-items-center gap-2">
								{!isMobileAndBelow && (
									<ShimmerImage
										src={job?.partnerLogo}
										defaultImage={defaultImage}
										alt={job?.partnerName}
										className={styles.company_logo}
										width={28}
										height={28}
									/>
								)}
								<span className="flex-grow-1 fs-small">
									{job?.partnerName || '----'}
								</span>
							</div>
							{job?.partnerAddress && (
								<div className="ms-auto d-flex align-items-center gap-2">
									<FontAwesome
										icon="location-dot"
										size="1x"
									/>
									<span className="fs-small flex-grow-1">
										{job?.partnerAddress?.postalCode}{' '}
										{job?.partnerAddress?.city}
									</span>
								</div>
							)}
						</div>
					</div>
				</Col>
			</Row>
		</article>
	);
};
